import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import TickerInputSection from './TickerInputSection';
import FundamentalsTable from './FundamentalsTable';
import EPSTrendsChart from './EPSTrendsChart';
import ResearchFinancialsTable from '../Research/ResearchFinancialsTable';
import PriceChartModal from '../Research/PriceChartModal';
import FairValueTable from './FairValueTable';
import ComparablesTable from './ComparablesTable';
import ElasticityForm from './ElasticityForm';
import ElasticityTable from './ElasticityTable';
import ExplorerTab from './ExplorerTab';
import EarningsAnalysisTable from './EarningsAnalysisTable';
import AnalysisHistory from './AnalysisHistory';
import ExportControls from './ExportControls';
import apiClient from '../../services/api';

function StockAnalyzerDashboard() {
  const { isAdmin } = useUser();
  const [analysisData, setAnalysisData] = useState({
    fundamentals: [],
    epseTrends: [],
    ratios: null,
    fairValue: [],
    comparables: [],
    elasticity: [],
    earnings: [],
    sessionId: null,
    sessionName: '',
    processing: false,
    errors: []
  });
  const [elasticityProcessing, setElasticityProcessing] = useState(false);
  const [earningsProcessing, setEarningsProcessing] = useState(false);
  const [lastAnalyzedTickers, setLastAnalyzedTickers] = useState([]);

  const [activeTab, setActiveTab] = useState('input'); // 'input', 'fundamentals', 'eps-trends', 'financials', 'history'
  const [watchedStocks, setWatchedStocks] = useState([]);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [ratiosYears, setRatiosYears] = useState(5); // 5 or 10 years
  const [ratiosProcessing, setRatiosProcessing] = useState(false);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [chartTicker, setChartTicker] = useState(null);

  useEffect(() => {
    // Load watched stocks on component mount
    loadWatchedStocks();
    loadAnalysisHistory();
  }, []);

  const loadWatchedStocks = async () => {
    try {
      const response = await apiClient.get('/stock-analyzer/watched-stocks/');
      setWatchedStocks(response.data.watched_stocks || []);
    } catch (error) {
      console.error('Error loading watched stocks:', error);
    }
  };

  const loadAnalysisHistory = async () => {
    try {
      const response = await apiClient.get('/stock-analyzer/history/');
      setAnalysisHistory(response.data.sessions || []);
    } catch (error) {
      console.error('Error loading analysis history:', error);
    }
  };

  const handleAnalysisSubmit = async (formData) => {
    setAnalysisData(prev => ({ ...prev, processing: true, errors: [] }));

    // Debug logging
    console.log('Submitting analysis request:', formData);

    try {
      const response = await apiClient.post('/stock-analyzer/analyze/', formData);
      console.log('Analysis response:', response.data);

      setAnalysisData({
        fundamentals: response.data.fundamentals_results || [],
        epsTrends: response.data.eps_trends_results || [],
        sessionId: response.data.session_id,
        sessionName: response.data.session_name,
        processing: false,
        errors: response.data.errors || []
      });

      // Store tickers for Explorer tab
      if (formData.tickers) {
        setLastAnalyzedTickers(formData.tickers);
      }

      // Switch to appropriate tab based on analysis types
      if (formData.analysis_types.includes('fundamentals')) {
        setActiveTab('fundamentals');
      } else if (formData.analysis_types.includes('eps_trends')) {
        setActiveTab('eps-trends');
      } else if (formData.analysis_types.includes('financials')) {
        setActiveTab('financials');
      } else if (formData.analysis_types.includes('fair_value')) {
        setActiveTab('fair-value');
      }

      // If financials was selected, fetch financials data separately
      if (formData.analysis_types.includes('financials')) {
        fetchRatiosData(formData.tickers, formData.years || ratiosYears);
      }

      // If fair_value was selected, fetch fair value data separately
      if (formData.analysis_types.includes('fair_value')) {
        fetchFairValueData(formData.tickers);
      }

      // If comparables was selected, fetch comparables data separately
      if (formData.analysis_types.includes('comparables')) {
        fetchComparablesData(formData.tickers);
      }

      // If earnings was selected, fetch earnings data separately
      if (formData.analysis_types.includes('earnings')) {
        fetchEarningsData(formData.tickers);
      }

      // Refresh history
      loadAnalysisHistory();

    } catch (error) {
      console.error('Analysis failed:', error);
      console.error('Error response:', error.response?.data);

      // Extract detailed error information
      const errorMessage = error.response?.data?.error || 'Analysis failed';
      const errorDetails = error.response?.data?.details || {};

      let detailedErrors = [errorMessage];

      // Add specific validation errors if available
      if (typeof errorDetails === 'object' && errorDetails !== null) {
        Object.entries(errorDetails).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            messages.forEach(msg => detailedErrors.push(`${field}: ${msg}`));
          } else {
            detailedErrors.push(`${field}: ${messages}`);
          }
        });
      }

      setAnalysisData(prev => ({
        ...prev,
        processing: false,
        errors: detailedErrors
      }));
    }
  };

  const handleAddToWatchlist = async (ticker, notes = '') => {
    try {
      await apiClient.post('/stock-analyzer/watched-stocks/', {
        ticker: ticker.toUpperCase(),
        notes
      });
      loadWatchedStocks();
    } catch (error) {
      console.error('Error adding to watchlist:', error);
    }
  };

  const fetchRatiosData = async (tickers, years) => {
    setRatiosProcessing(true);
    try {
      const response = await apiClient.post('/stock-analyzer/ratios/', {
        tickers,
        years
      });
      console.log('Ratios response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        ratios: response.data
      }));
    } catch (error) {
      console.error('Ratios fetch failed:', error);
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, 'Failed to fetch historical ratios']
      }));
    } finally {
      setRatiosProcessing(false);
    }
  };

  const handleRatiosYearsChange = (years) => {
    setRatiosYears(years);
    // Re-fetch ratios data if we have tickers
    if (analysisData.ratios?.tickers?.length > 0) {
      fetchRatiosData(analysisData.ratios.tickers, years);
    }
  };

  const handleTickerClick = (ticker) => {
    setChartTicker(ticker);
    setChartModalOpen(true);
  };

  const fetchFairValueData = async (tickers) => {
    try {
      const response = await apiClient.post('/stock-analyzer/fair-value/', { tickers });
      console.log('Fair value response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        fairValue: response.data.fair_value_results || []
      }));
    } catch (error) {
      console.error('Fair value fetch failed:', error);
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, 'Failed to fetch fair value data']
      }));
    }
  };

  const fetchComparablesData = async (tickers) => {
    try {
      const response = await apiClient.post('/stock-analyzer/comparables/', { tickers });
      console.log('Comparables response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        comparables: response.data.comparables_results || []
      }));
    } catch (error) {
      console.error('Comparables fetch failed:', error);
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, 'Failed to fetch comparables data']
      }));
    }
  };

  const fetchEarningsData = async (tickers) => {
    setEarningsProcessing(true);
    try {
      const response = await apiClient.post('/stock-analyzer/earnings/', { tickers });
      console.log('Earnings response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        earnings: response.data.earnings_results || []
      }));
    } catch (error) {
      console.error('Earnings fetch failed:', error);
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, 'Failed to fetch earnings data']
      }));
    } finally {
      setEarningsProcessing(false);
    }
  };

  const fetchElasticityData = async (requestData) => {
    setElasticityProcessing(true);
    setAnalysisData(prev => ({ ...prev, errors: [] }));
    try {
      const response = await apiClient.post('/stock-analyzer/elasticity/', requestData);
      console.log('Elasticity response:', response.data);
      setAnalysisData(prev => ({
        ...prev,
        elasticity: response.data.elasticity_results || []
      }));
    } catch (error) {
      console.error('Elasticity fetch failed:', error);
      const errorMessage = error.response?.data?.error || 'Failed to fetch elasticity data';
      setAnalysisData(prev => ({
        ...prev,
        errors: [...prev.errors, errorMessage]
      }));
    } finally {
      setElasticityProcessing(false);
    }
  };


  const handleExport = async (exportOptions) => {
    try {
      const response = await apiClient.post('/stock-analyzer/export/', exportOptions, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : 'stock_analysis.csv';

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'input':
        return (
          <TickerInputSection
            onAnalysisSubmit={handleAnalysisSubmit}
            watchedStocks={watchedStocks}
            onAddToWatchlist={handleAddToWatchlist}
            processing={analysisData.processing}
            errors={analysisData.errors}
          />
        );

      case 'fundamentals':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Fundamentals Analysis</h2>
              <ExportControls
                sessionId={analysisData.sessionId}
                analysisTypes={['fundamentals']}
                onExport={handleExport}
              />
            </div>
            <FundamentalsTable
              data={analysisData.fundamentals}
              onAddToWatchlist={handleAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        );

      case 'eps-trends':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">EPS Trends Analysis</h2>
              <ExportControls
                sessionId={analysisData.sessionId}
                analysisTypes={['eps_trends']}
                onExport={handleExport}
              />
            </div>
            <EPSTrendsChart
              data={analysisData.epsTrends}
              onAddToWatchlist={handleAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        );

      case 'financials':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Historical Financials</h2>
              {ratiosProcessing && (
                <div className="flex items-center text-gray-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Loading financials...
                </div>
              )}
            </div>
            <ResearchFinancialsTable
              data={analysisData.ratios}
              years={ratiosYears}
              onYearsChange={handleRatiosYearsChange}
              onTickerClick={handleTickerClick}
            />
          </div>
        );

      case 'fair-value':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Fair Value Analysis</h2>
            </div>
            <FairValueTable
              data={analysisData.fairValue}
              onAddToWatchlist={handleAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        );

      case 'comparables':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Comparables Analysis</h2>
            </div>
            <ComparablesTable
              data={analysisData.comparables}
              onAddToWatchlist={handleAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        );

      case 'elasticity':
        return (
          <div className="space-y-8">
            <ElasticityForm
              onSubmit={fetchElasticityData}
              processing={elasticityProcessing}
            />
            {analysisData.elasticity.length > 0 && (
              <div>
                <div className="border-t border-gray-700 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Elasticity Results</h3>
                  <ElasticityTable
                    data={analysisData.elasticity}
                    onAddToWatchlist={handleAddToWatchlist}
                    watchedStocks={watchedStocks}
                  />
                </div>
              </div>
            )}
            {analysisData.errors.length > 0 && (
              <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
                <h3 className="text-red-400 font-medium mb-2">Errors:</h3>
                <ul className="text-red-300 text-sm space-y-1">
                  {analysisData.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'explorer':
        return (
          <ExplorerTab
            tickers={lastAnalyzedTickers}
            onNavigateToInput={() => setActiveTab('input')}
          />
        );

      case 'earnings':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Earnings Analysis</h2>
              {earningsProcessing && (
                <div className="flex items-center text-gray-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Loading earnings data...
                </div>
              )}
            </div>
            <EarningsAnalysisTable
              data={analysisData.earnings}
              onAddToWatchlist={handleAddToWatchlist}
              watchedStocks={watchedStocks}
            />
          </div>
        );

      case 'history':
        return (
          <AnalysisHistory
            sessions={analysisHistory}
            onLoadSession={(sessionId) => {
              // Load session data - could implement this later
              console.log('Load session:', sessionId);
            }}
            onDeleteSession={(sessionId) => {
              // Delete session - could implement this later
              console.log('Delete session:', sessionId);
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold mb-2">Stock Analyzer</h1>
            <p className="text-gray-400">
              Comprehensive fundamental analysis and EPS trends tracking
            </p>
          </div>
          {isAdmin && (
            // Admin-only entry point. The Scoring Builder is hidden from
            // Plus/Premium users (TierFeatureAccess disabled in
            // 0022_disable_scoring_builder_for_users) while it's polished;
            // is_staff bypass keeps the route reachable for admins.
            <Link
              to="/stock-analyzer/scoring-builder"
              className="px-3 py-2 text-sm rounded-md border border-blue-500/60 text-blue-300 hover:bg-blue-600/20 transition whitespace-nowrap"
              title="Admin only — feature is hidden from end users while in development"
            >
              Open Scoring Builder →
            </Link>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-700 mb-6">
          <nav className="flex space-x-8">
            {[
              { key: 'input', label: 'Analysis Input', icon: '🔍' },
              { key: 'fundamentals', label: 'Fundamentals', icon: '📊' },
              { key: 'eps-trends', label: 'EPS Trends', icon: '📈' },
              { key: 'financials', label: 'Financials', icon: '📉' },
              { key: 'fair-value', label: 'Fair Value', icon: '💰' },
              { key: 'comparables', label: 'Comparables', icon: '📏' },
              { key: 'elasticity', label: 'Options Elasticity', icon: '⚡' },
              { key: 'explorer', label: 'Options Explorer', icon: '🔭' },
              { key: 'earnings', label: 'Earnings', icon: '📅' },
              { key: 'history', label: 'History', icon: '📋' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="bg-gray-800 rounded-lg p-6">
          {renderTabContent()}
        </div>

        {/* Status Bar */}
        {analysisData.processing && (
          <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing analysis...
            </div>
          </div>
        )}

        {/* Error Display */}
        {analysisData.errors.length > 0 && (
          <div className="fixed bottom-4 left-4 max-w-md">
            {analysisData.errors.map((error, index) => (
              <div key={index} className="bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg mb-2">
                {error}
              </div>
            ))}
          </div>
        )}

        {/* Price Chart Modal */}
        <PriceChartModal
          isOpen={chartModalOpen}
          onClose={() => setChartModalOpen(false)}
          ticker={chartTicker}
        />
      </div>
    </div>
  );
}

export default StockAnalyzerDashboard;